import {
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type DefaultProcessStepSection,
  type DefaultProcessStepSections,
  findDefaultProcessStepSection,
  isDefaultProcessStepSection,
  moveDefaultProcessStepToSection,
  reorderDefaultProcessStep,
} from "#modules/onboardingV2/domain/splitDefaultProcessSteps";

const defaultProcessStepsCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  const itemCollisions = pointerCollisions.filter(
    (collision) => !isDefaultProcessStepSection(String(collision.id)),
  );

  if (itemCollisions.length > 0) {
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        itemCollisions.some((collision) => collision.id === container.id),
      ),
    });
  }

  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  return closestCenter(args);
};

type UseDefaultProcessStepsDragParams<T extends { id: string }> = {
  beginning: T[];
  end: T[];
  onCommit: (sections: DefaultProcessStepSections<T>) => void;
};

export const useDefaultProcessStepsDrag = <T extends { id: string }>({
  beginning,
  end,
  onCommit,
}: UseDefaultProcessStepsDragParams<T>) => {
  const [dragSections, setDragSections] = useState<DefaultProcessStepSections<T> | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const recentlyMovedSectionRef = useRef(false);
  const sections = dragSections ?? { beginning, end };
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const snapshot: DefaultProcessStepSections<T> = {
        beginning: [...beginning],
        end: [...end],
      };
      recentlyMovedSectionRef.current = false;
      sectionsRef.current = snapshot;
      setActiveId(String(event.active.id));
      setDragSections(snapshot);
    },
    [beginning, end],
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id;
    if (!overId || event.active.id === overId || recentlyMovedSectionRef.current) {
      return;
    }

    const activeIdValue = String(event.active.id);
    const overIdValue = String(overId);

    setDragSections((current) => {
      const source = current ?? sectionsRef.current;
      const next = moveDefaultProcessStepToSection({
        sections: source,
        activeId: activeIdValue,
        overId: overIdValue,
        placeAfter: false,
      });
      if (!next) {
        return current;
      }

      recentlyMovedSectionRef.current = true;
      sectionsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      recentlyMovedSectionRef.current = false;
    });

    return () => cancelAnimationFrame(frame);
  }, [dragSections]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const current = sectionsRef.current;
      const overId = event.over ? String(event.over.id) : null;
      const activeIdValue = String(event.active.id);
      let next = current;

      if (overId) {
        const moved = moveDefaultProcessStepToSection({
          sections: next,
          activeId: activeIdValue,
          overId,
          placeAfter: false,
        });
        if (moved) {
          next = moved;
        }

        const section: DefaultProcessStepSection | null = findDefaultProcessStepSection(
          next,
          activeIdValue,
        );
        if (section && !isDefaultProcessStepSection(overId)) {
          const reordered = reorderDefaultProcessStep(next[section], activeIdValue, overId);
          if (reordered) {
            next = { ...next, [section]: reordered };
          }
        }
      }

      onCommit(next);
      setDragSections(null);
      setActiveId(null);
      recentlyMovedSectionRef.current = false;
    },
    [onCommit],
  );

  const handleDragCancel = useCallback(() => {
    setDragSections(null);
    setActiveId(null);
    recentlyMovedSectionRef.current = false;
  }, []);

  const activeStep =
    activeId === null
      ? null
      : ([...sections.beginning, ...sections.end].find(
          (processStep) => processStep.id === activeId,
        ) ?? null);

  return {
    sections,
    activeStep,
    sensors,
    collisionDetection: defaultProcessStepsCollisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
};
